// WizardShell — common modal frame all wizards mount inside. Keeps every
// wizard visually consistent (title bar, footer with Cancel/Submit) so
// individual wizards focus on their per-type form.

import React from 'react'

// Palette handles, resolved from the app's theme tokens (src/styles/tokens.css,
// src/lib/palettes/tokens.js) instead of the hardcoded dark literals these used
// to be (ENG-1046). Every wizard reads its colors through this map, so all of
// them stayed dark on a light palette while the chrome around them repainted —
// GenerateBodyAnimWizard's header, the one part already using var(--m-bg), was
// the only piece that followed the theme.
//
// These are `var(...)` strings, not resolved colors: they go straight into
// inline styles, so the browser re-resolves them whenever paletteStore rewrites
// the custom properties on <html>. Nothing here re-renders on a theme switch.
const C = {
  scrim:   'var(--m-overlay)',
  bg:      'var(--m-panel)',
  bgInset: 'var(--m-bg)',      // recessed surface on the panel (inputs, sub-boxes)
  border:  'var(--m-line)',
  text:    'var(--m-fg)',
  dim:     'var(--m-fg-2)',
  faint:   'var(--m-fg-3)',
  primary: 'var(--m-accent)',
  onPrim:  'var(--m-accent-ink)',
  danger:  'var(--m-danger)',
  warn:    'var(--m-warn)',
}

export default function WizardShell({
  title,
  subtitle,
  children,
  onCancel,
  onSubmit,
  submitLabel = 'Create',
  submitDisabled = false,
  error,
}) {
  return (
    <div
      onMouseDown={e => { if (e.target === e.currentTarget) onCancel?.() }}
      style={{
        position: 'fixed', inset: 0, background: C.scrim,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1000,
      }}
    >
      <div style={{
        background: C.bg, border: `1px solid ${C.border}`, borderRadius: 6,
        width: 'min(560px, 92vw)', maxHeight: '88vh',
        display: 'flex', flexDirection: 'column',
        boxShadow: 'var(--m-shadow-card)',
      }}>
        <div style={{
          padding: '14px 18px', borderBottom: `1px solid ${C.border}`,
        }}>
          <div style={{ color: C.text, fontFamily: 'var(--m-font)', fontSize: 13, fontWeight: 700, letterSpacing: '.06em' }}>
            {title}
          </div>
          {subtitle && (
            <div style={{ color: C.dim, fontFamily: 'var(--m-font)', fontSize: 11, marginTop: 4 }}>
              {subtitle}
            </div>
          )}
        </div>
        <div style={{ padding: 18, overflowY: 'auto', flex: 1 }}>
          {children}
          {error && (
            <div style={{ marginTop: 12, color: C.danger, fontFamily: 'var(--m-font)', fontSize: 11 }}>
              {error}
            </div>
          )}
        </div>
        <div style={{
          padding: '12px 18px', borderTop: `1px solid ${C.border}`,
          display: 'flex', gap: 8, justifyContent: 'flex-end',
        }}>
          <button onClick={onCancel} style={btnSecondary}>Cancel</button>
          <button onClick={onSubmit} disabled={submitDisabled} style={{
            ...btnPrimary, opacity: submitDisabled ? 0.5 : 1,
            cursor: submitDisabled ? 'default' : 'pointer',
          }}>
            {submitLabel}
          </button>
        </div>
      </div>
    </div>
  )
}

const btnBase = {
  padding: '6px 14px', borderRadius: 4, fontFamily: 'var(--m-font)', fontSize: 11,
  letterSpacing: '.06em', textTransform: 'uppercase', fontWeight: 600,
}
const btnPrimary = { ...btnBase, background: C.primary, border: `1px solid ${C.primary}`, color: C.onPrim, cursor: 'pointer' }
const btnSecondary = { ...btnBase, background: 'transparent', border: `1px solid ${C.border}`, color: C.dim, cursor: 'pointer' }

export const tokens = C
export const inputStyle = {
  width: '100%', boxSizing: 'border-box',
  background: C.bgInset, border: `1px solid ${C.border}`,
  borderRadius: 4, padding: '6px 10px',
  color: C.text, fontSize: 12, fontFamily: 'var(--m-font)', outline: 'none',
}
export const labelStyle = {
  fontFamily: 'var(--m-font)', fontSize: 9.5, fontWeight: 700,
  letterSpacing: '.1em', textTransform: 'uppercase',
  color: C.dim, marginBottom: 4, marginTop: 10, display: 'block',
}
